import React, { useMemo } from 'react';
import cx from 'classnames';
import { StreamingImageTreeNodeData } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { hydrateRichTextVariables, renderRichTextToPlainString, normalizeStreamingRichText } from '../../richtext/ast';
import { transformToContainerQueries } from '../utils/transformBreakpointClasses';
import defaultImage from './images/default-image.png';

type ImageProps = StreamingImageTreeNodeData & {
  id: string;
  editorClassName?: string;
  overrideVariables?: Partial<Record<string, string>>;
  staticAssetsBaseURL?: string | null;
  isEditor?: boolean;
};

export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      id,
      className,
      editorClassName,
      src,
      alt,
      overrideVariables,
      imageHref: href,
      openInNewTab,
      staticAssetsBaseURL,
      isEditor = false,
      ...props
    },
    ref,
  ) => {
    // Normalize streaming RichText to complete RichText for safe processing
    const normalizedSrc = useMemo(() => normalizeStreamingRichText(src), [src]);
    const normalizedHref = useMemo(() => (href ? normalizeStreamingRichText(href) : undefined), [href]);

    const resolvedSrc = useMemo(() => {
      let value = normalizedSrc;

      // Hydrate RichText variables if needed
      if (typeof value !== 'string') {
        value = hydrateRichTextVariables(value, overrideVariables || {});
      }

      // Convert to plain string if still RichText
      if (typeof value !== 'string') {
        value = renderRichTextToPlainString(value);
      }

      // Prepend staticAssetsBaseURL if available and src is relative
      if (staticAssetsBaseURL && value && !value.startsWith('http')) {
        return `https://${staticAssetsBaseURL}${value}`;
      }

      return value;
    }, [normalizedSrc, overrideVariables, staticAssetsBaseURL]);

    const resolvedHref = useMemo(() => {
      if (!normalizedHref) return normalizedHref;

      let value = normalizedHref;

      // Hydrate RichText variables if needed
      if (typeof value !== 'string') {
        value = hydrateRichTextVariables(value, overrideVariables || {});
      }

      // Convert to plain string if still RichText
      if (typeof value !== 'string') {
        value = renderRichTextToPlainString(value);
      }

      return value;
    }, [normalizedHref, overrideVariables]);

    const onError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const eventTarget = event.target as HTMLImageElement;
      eventTarget.src = defaultImage;
      eventTarget.onerror = null;
    };

    const finalSrc = resolvedSrc || defaultImage;

    const imgClassName = cx(transformToContainerQueries(className, { isEditor }), editorClassName);

    if (resolvedHref) {
      return (
        <a href={resolvedHref} target={openInNewTab ? '_blank' : '_self'} rel="noreferrer">
          <img id={id} ref={ref} src={finalSrc} alt={alt} className={imgClassName} onError={onError} {...props} />
        </a>
      );
    }

    return <img id={id} ref={ref} src={finalSrc} alt={alt} className={imgClassName} onError={onError} {...props} />;
  },
);

Image.displayName = 'Image';
